


































































































import {
  defineComponent,
  nextTick,
  onMounted,
  ref,
  toRefs,
  computed,
  watch,
  PropType,
  onBeforeUnmount,
  vue3Model,
  Ref
} from '~/utils/nuxt3-migration'
import CCalendar from '~/components/shared/configurable/date/CCalendar.vue'
import {
  parseDate,
  format,
  getDateLocale,
  isISODate,
  parseISODate
} from '~/utils/date'

import { useFormComponent } from '~/compositions/form-component'
import { DropdownPosition } from '~/models/app/dropdown'
import { useI18n } from '~/compositions/i18n'
import { ciCalendar } from '~/icons/source/regular/calendar'
import { SIMPLE_DATETIME_FORMAT } from '~/constants/date'

export default defineComponent({
  model: vue3Model,
  props: {
    modelValue: {
      type: [Date, String, Object],
      default: () => null
    },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    range: { type: Boolean, default: false },
    resetButton: { type: Boolean, default: false },
    resetButtonLabel: { type: String, default: null },
    secondaryButton: { type: Boolean, default: false },
    secondaryButtonLabel: { type: String, default: null },
    applyButton: { type: Boolean, default: false },
    locale: { type: [String, Object], default: 'el' },
    disabledDates: { type: Array, default: () => [] },
    inputDateFormat: { type: String, default: 'dd/MM/yyyy' },
    textDateFormat: { type: String, default: 'dd/MM/yyyy' },
    max: { type: [Date, String], default: null },
    min: { type: [Date, String], default: null },
    valueAsDate: { type: Boolean, default: null },
    availableDates: { type: Array, default: () => [] },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'md'
    },
    closeOnSelect: { type: Boolean, default: true },
    state: { type: Boolean, default: null },
    withoutIcon: { type: Boolean, default: false },
    skidding: { type: Number, default: null },
    inputClass: { type: [String, Array, Object], default: null },
    placement: {
      type: String as PropType<DropdownPosition>,
      default: 'bottom-start'
    },
    mode: { type: String, default: 'date' },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CCalendar
  },
  setup(props, { emit }) {
    const {
      size,
      closeOnSelect,
      disabled,
      inputDateFormat,
      range,
      valueAsDate,
      textDateFormat,
      locale: localeProp,
      mode,
      hasError
    } = toRefs(props)
    const { locale } = useI18n()

    const internalInputDateFormat = ref(
      mode.value === 'dateTime' ? SIMPLE_DATETIME_FORMAT : inputDateFormat.value
    )

    const internalTextDateFormat = ref(
      mode.value === 'dateTime' ? SIMPLE_DATETIME_FORMAT : textDateFormat.value
    )

    const internalDate: Ref<Date | string | object> = ref()
    const isVCalendarMounted = ref(false)
    internalDate.value = normalizedDate(props.modelValue)
    const isDatepickerVisible = ref(false)
    const dDown = ref(null)
    const datepickerTemplateRef = ref()

    const { controlClasses } = useFormComponent(
      ref(''),
      hasError,
      ref(false),
      ref(false),
      'input'
    )

    watch(
      () => props.modelValue,
      (newDate, oldDate) => {
        if (newDate === oldDate) {
          return
        }
        internalDate.value = normalizedDate(newDate)
      }
    )

    onMounted(() => {
      document.addEventListener('mousedown', handleClick)
    })
    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleClick)
    })

    const formatedInternalDate = computed(() => {
      return getFormatedDate(internalInputDateFormat.value)
    })

    const formatedTextDate = computed(() => {
      return getFormatedDate(internalTextDateFormat.value)
    })

    function getFormatedDate(formatString: string) {
      if (internalDate.value && range.value) {
        const { start, end } = internalDate.value as { end: Date; start: Date }

        if ((!start || !end) && typeof internalDate.value === 'object') {
          return {}
        }
        return `${formatDate(start, formatString)} - ${formatDate(
          end,
          formatString
        )}`
      }
      return formatDate(internalDate.value as Date, formatString)
    }

    function formatDate(
      date: Date,
      formatString: string = internalInputDateFormat.value
    ) {
      return format(date, formatString, { locale })
    }

    function handleDateInput(date: Date) {
      if (
        isDatepickerVisible.value &&
        closeOnSelect.value &&
        mode.value === 'date'
      ) {
        dDown.value.hide()
        isDatepickerVisible.value = false
      }

      emit(
        'update:modelValue',
        valueAsDate.value ? date : formatedInternalDate.value
      )
    }
    async function handleInputClick() {
      if (!disabled.value) {
        dDown.value.show()
        await nextTick()
      }
    }

    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement
      const parent = target?.parentNode as HTMLElement
      const isDatePickerButton =
        parent?.dataset?.attribute === 'date-picker-button' ||
        target.dataset?.attribute === 'date-picker-button'

      if (disabled.value || isDatePickerButton) {
        return
      }
      const isClickDatepicker = !!(e.target as HTMLElement).closest(
        '.c-datepicker'
      )
      if (
        (isDatepickerVisible.value &&
          !closeOnSelect.value &&
          !isClickDatepicker) ||
        (isDatepickerVisible.value && !isClickDatepicker)
      ) {
        closeDatepicker()
      }
    }

    function closeDatepicker() {
      isDatepickerVisible.value = false
      dDown.value.hide()
    }

    function handleHidden() {
      emit('hidden')
    }
    function handleShown() {
      isDatepickerVisible.value = true
      emit('shown')
    }

    const sizeClasses = computed(() => {
      switch (size.value) {
        case 'sm': {
          return [
            'tw-h-[32px]',
            'tw-min-h-[32px]',
            'tw-text-base',
            'tw-rounded-md'
          ]
        }
        case 'lg': {
          return [
            'tw-h-[48px]',
            'tw-min-h-[48px]',
            'tw-text-lg',
            'tw-rounded-xl'
          ]
        }
        case 'md':
        default: {
          return [
            'tw-h-[40px]',
            'tw-min-h-[40px]',
            'tw-text-base',
            'tw-rounded-lg'
          ]
        }
      }
    })

    const computedClasses = computed(() => {
      const classes = []
      if (disabled.value) {
        classes.push(
          ...[
            'tw-bg-grey-300',
            'tw-cursor-not-allowed',
            'tw-text-grey-600',
            'tw-border-grey-300',
            'tw-opacity-[0.7]',
            'pointer-events-none'
          ]
        )
      } else {
        classes.push(...['tw-bg-white', 'tw-cursor-pointer'])
        classes.push(...controlClasses.value)
      }

      if (isDatepickerVisible.value && !disabled.value) {
        if (props.hasError) {
          classes.push('tw-ring-red-500', '!tw-border-red-500')
        } else {
          classes.push('tw-ring-blue-500', '!tw-border-blue-500')
        }
        classes.push('tw-ring-1')
      }

      return [...sizeClasses.value, ...classes]
    })

    function handleCalendarShow() {
      isVCalendarMounted.value = true
    }

    function handleClickReset() {
      internalDate.value = null
      emit('update:modelValue', null)
    }
    function handleClickSecondary() {
      dDown.value.hide()
      isDatepickerVisible.value = false
      emit('secondary-button-click')
    }

    const nothingSelected = computed(
      () =>
        !internalDate.value ||
        (Array.isArray(internalDate.value) && internalDate.value.length === 0)
    )

    function normalizedDate(dt: string | Date | object) {
      if (typeof dt === 'string' && dt.match(/ - /)) {
        const locale = getDateLocale(localeProp)
        const [start, end] = dt.replace(/ /g, '').split('-')
        return {
          start: parseDate(start, internalInputDateFormat.value, locale),
          end: parseDate(end, internalInputDateFormat.value, locale)
        }
      } else if (isISODate(dt)) {
        return parseISODate(dt)
      } else if (typeof dt === 'string') {
        return parseDate(
          dt,
          internalInputDateFormat.value,
          getDateLocale(localeProp)
        )
      } else if (
        (dt as any) instanceof Date ||
        (dt && typeof dt === 'object')
      ) {
        return dt
      }
      return null
    }

    return {
      datepickerTemplateRef,
      internalDate,
      handleDateInput,
      handleInputClick,
      isDatepickerVisible,
      dDown,
      handleShown,
      sizeClasses,
      handleClick,
      handleHidden,
      formatedTextDate,
      ciCalendar,
      handleCalendarShow,
      isVCalendarMounted,
      handleClickReset,
      handleClickSecondary,
      controlClasses,
      computedClasses,
      nothingSelected,
      closeDatepicker
    }
  }
})
